import { Alignment, ArrayUtil, DateUtil, FileUtil, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button } from "../../components/button/Button";
import { ButtonProps } from "../../components/button/ButtonProps";
import { Component } from "../..";
import { Cursor } from "../../base/Cursor";
import { Label } from "../../components/label/Label";
import { LayoutHeader } from "../../components/layoutheader/LayoutHeader";
import { LayoutProvidedReportDecoratorProps } from "./LayoutProvidedReportDecoratorProps";
import { Panel } from "../../components/panel/Panel";
import { PanelProps } from "../../components/panel/PanelProps";
import { ReportData } from "./ReportData";
import { ReportPreviewDataResolver } from "./ReportPreviewDataResolver";
import { ReportPreviewTabSource } from "./ReportPreviewTabSource";
import { ReportViewer } from "./ReportViewer";
import { ReportViewerTab } from "./ReportViewerTab";
import { Tab } from "../../components/tabset/Tab";
import { Tabset } from "../../components/tabset/Tabset";
import { TabStyle } from "../../components/tabset/TabsetProps";

export class ReportPreview extends Panel {
    private buttonDownloadExcel: Button;
    private buttonDownloadPdf: Button;
    private _data: ReportPreviewTabSource[];
    private _displaySingleResultInTabset: boolean;
    private static readonly DIVIDER_REPORT_NAME = "__divider";
    private readonly excelDownloadTooltipEnabled = "Click to download this report as a Microsoft Excel file.";
    private readonly excelDownloadTooltipDisabled = "Microsoft Excel file unavailable.";
    private header: LayoutHeader;
    private layoutProvidedProps: Partial<LayoutProvidedReportDecoratorProps>;
    private panelDownload: Panel;
    private placeholderReportViewer: ReportViewer;
    private readonly pdfDownloadTooltipEnabled = "Click to download this report as a PDF.";
    private readonly pdfDownloadTooltipDisabled = "PDF unavailable.";
    public reportFieldName = "reports";
    private tabs: Tab[];
    private tabsetReportViewers: Tabset;

    constructor(reportProps: string|Partial<LayoutProvidedReportDecoratorProps>, panelProps?: Partial<PanelProps>) {
        super({
            id: "panelReportPreview",
            fillRow: true,
            fillHeight: true,
            margin: 0,
            padding: 0,
            paddingLeft: 4,
            ...panelProps
        });
        this.resolveProvidedProps(reportProps);
        this.setupHeader();
        this.reportName = this.layoutProvidedProps.reportPreviewTitle;
        this.add(this.header);
        this.placeholderReportViewer = new ReportViewer({ padding: 0 });
        this.displayPlaceholderViewer();
    }

    private resolveProvidedProps(props: string|Partial<LayoutProvidedReportDecoratorProps>) {
        if (typeof props === "string")
            this.layoutProvidedProps = { reportPreviewTitle: props };
        else
            this.layoutProvidedProps = props ?? {};
    }

    public set reportName(value: string) {
        this.header.title = StringUtil.isEmptyString(value) === true ? "" : value;
    }

    private setupHeader() {
        this.header = new LayoutHeader({
            id: "layoutheaderReportPreview",
            fillRow: true,
            padding: 0,
            margin: 0
        });
        this.header.labelTitlePaddingLeft = 0;
        this.header.addlPanelRight.paddingRight = 0;
        this.createPanelDownload();
        this.header.addRightTools(this.panelDownload);
        this.addAddlHeaderTools();
    }

    private createPanelDownload() {
        this.createButtonDownloadPdf();
        this.createButtonDownloadExcel();
        this.panelDownload = Panel.createNoMarginNoPaddingPanel({ id: "panelDownload" });
        this.panelDownload.add(this.createExportLabel(), this.buttonDownloadPdf, this.buttonDownloadExcel);
    }

    // Only show the download options if at least one tab is displaying downloadable content
    private showHidePanelDownload() {
        this.panelDownload.visible = this.tabs.some(tab => tab instanceof ReportViewerTab);
    }

    private addAddlHeaderTools() {
        this.reportName = this.layoutProvidedProps.reportPreviewTitle;
        const addlLeftTools = this.layoutProvidedProps.headerAddlLeftTools ?? [];
        this.addLeftHeaderTools(...addlLeftTools);
        const addlRightTools = this.layoutProvidedProps.headerAddlRightTools ?? [];
        this.addRightHeaderTools(...addlRightTools);
        const addlBottomTools = this.layoutProvidedProps.headerAddlBottomTools ?? [];
        this.addBottomHeaderTools(...addlBottomTools);
    }

    public addLeftHeaderTools(...components: Component[]) {
        this.header.addLeftTools(...components);
    }

    public addRightHeaderTools(...components: Component[]) {
        this.header.addRightTools(...components);
        for (const addlRightTool of this.header.addlPanelRight.components) {
            addlRightTool.marginRight = addlRightTool.marginRight;
        }
        const lastTool = ArrayUtil.getLastElement(this.header.addlPanelRight.components);
        if (lastTool != null)
            lastTool.style.marginRight = 0;
    }

    public insertFirstRightHeaderTool(component: Component) {
        this.header.addlPanelRight.insert(component, 0);
    }

    public addBottomHeaderTools(...components: Component[]) {
        this.header.addBottomTools(...components);
    }

    public set tabCreationCallack(value: (tab: ReportViewerTab) => void) {
        this.layoutProvidedProps.tabCreationCallback = value;
    }

    private createExportLabel(): Label {
        return new Label({
            id: "labelExport",
            text: "Export as:",
            color: "subtle.darker",
            fontBold: true,
            rowBreak: false
        });
    }

    private createButtonDownloadPdf() {
        this.buttonDownloadPdf = new Button({
            ...this.getDownloadButtonProps(),
            id: "buttonDownloadPdf",
            imageName: ImageName.documentPdf,
            tooltip: this.pdfDownloadTooltipDisabled,
            rowBreak: false,
            onClick: () => this.downloadPdf(),
            cursor: null
        });
    }

    private downloadPdf() {
        const todaysDate = DateUtil.formatDate(new Date(), "MM/dd/yyyy");
        FileUtil.downloadBase64AsFile(this.visibleReportPdfBytes, `Report ${this.visibleReportName} ${todaysDate}.pdf`);
    }

    private createButtonDownloadExcel() {
        this.buttonDownloadExcel = new Button({
            ...this.getDownloadButtonProps(),
            id: "buttonDownloadExcel",
            imageName: ImageName.documentExcel,
            tooltip: this.excelDownloadTooltipDisabled,
            onClick: () => this.downloadExcel(),
            cursor: null,
            rowBreak: false
        });
    }

    private downloadExcel() {
        const todaysDate = DateUtil.formatDate(new Date(), "MM/dd/yyyy");
        FileUtil.downloadBase64AsFile(this.visibleReportExcelBytes, `Report ${this.visibleReportName} ${todaysDate}.xlsx`);
    }

    private getDownloadButtonProps(): Partial<ButtonProps> {
        return {
            enabled: false,
            height: 40,
            width: 40,
            padding: 0,
            borderWidth: 0,
            imageHeight: 32,
            imageWidth: 32
        };
    }

    private enableDownloads() {
        this.enableExcelDownload();
        this.enablePdfDownload();
    }

    private enablePdfDownload() {
        const enable = !StringUtil.isEmptyString(this.visibleReportPdfBytes);
        this.buttonDownloadPdf.enabled = enable;
        this.buttonDownloadPdf.cursor = enable ? Cursor.POINTER : null;
        this.buttonDownloadPdf.imageColor = enable ? "primary" : null;
        this.buttonDownloadPdf.tooltip = enable ? this.pdfDownloadTooltipEnabled : this.pdfDownloadTooltipDisabled;
    }

    private enableExcelDownload() {
        const enable = !StringUtil.isEmptyString(this.visibleReportExcelBytes);
        this.buttonDownloadExcel.enabled = enable;
        this.buttonDownloadExcel.cursor = enable ? Cursor.POINTER : null;
        this.buttonDownloadExcel.imageColor = enable ? "primary" : null;
        this.buttonDownloadExcel.tooltip = enable ? this.excelDownloadTooltipEnabled :
            this.excelDownloadTooltipDisabled;
    }

    private removeContent() {
        for (let x = this.components.length - 1 ; x >= 0 ; x--) {
            const component = this.components[x];
            if (component !== this.header) {
                this.remove(component);
            }
        }
    }

    private displayPlaceholderViewer() {
        this.removeContent();
        this.add(this.placeholderReportViewer);
    }

    private displayPreviewNotAvailableInPlaceholder() {
        // Causes 'preview not available' message to be displayed
        this.placeholderReportViewer.imageBytes = null;
    }

    private displaySingleTab(tab: Tab) {
        this.removeContent();
        if (tab != null) {
            this.add(tab);
        } else {
            this.displayPlaceholderViewer();
            this.displayPreviewNotAvailableInPlaceholder();
        }
    }

    private displayTabset() {
        this.removeContent();
        this.initializeTabset();
        this.add(this.tabsetReportViewers);
    }

    private initializeTabset() {
        if (this.tabsetReportViewers == null) {
            this.tabsetReportViewers = new Tabset({
                id: this.createInnerComponentId("tabsetReportViewers"),
                fillHeight: true,
                fillRow: true,
                tabAlignment: Alignment.LEFT,
                tabHeadingMaxWidth: 200,
                tabStyle: TabStyle.UNDERLINED
            });
            this.tabsetReportViewers.addAfterTabSelectionListener(() => {
                this.enableDownloads();
            });
        }
    }

    public get tabCount(): number {
        return ArrayUtil.getLength(this.tabs);
    }

    public override isEmpty(): boolean {
        return this.tabCount === 0;
    }

    public get data(): ReportPreviewTabSource[] {
        return this._data;
    }

    /**
     * @param value An array containing a combination of ReportData objects and any ad-hoc Tabs that should be rendered
     */
    public set data(value: (any|ReportPreviewTabSource)[]) {
        this._data = new ReportPreviewDataResolver(this.reportFieldName).resolve(value);
        this.displayReportData();
    }

    public clearData() {
        this.data = null;
    }

    private displayReportData() {
        this.displayPlaceholderViewer();
        this.placeholderReportViewer.clear();
        this.tabsetReportViewers?.removeAll();
        try {
            // Loop through the tab sources in data:
            // - If the tab source is an ad-hoc tab, add it as-is.
            // - If the tab source is a ReportData indicating a divider should be displayed, mark the previous tab to
            //   indicate it should display the divider border (which can't be done until the tab is added to the
            //   tabset).
            // - If the tab source is a non-divider ReportData, create a ReportViewerTab and add it to the tabs to be
            this.tabs = [];
            this.data.forEach(tabSource => {
                if (tabSource instanceof Tab) {
                    this.tabs.push(tabSource);
                } else if (this.isDivider(tabSource)) {
                    const previousTab: Tab = ArrayUtil.getLastElement(this.tabs);
                    if (previousTab != null) {
                        previousTab.displaysDividerBorder = true;
                    }
                } else {
                    const tab = new ReportViewerTab(tabSource);
                    this.tabs.push(tab);
                }
            });

            // If there are no tabs to display, the placeholder viewer will be displayed with 'no preview available'
            // message
            if (this.tabCount === 0) {
                this.displayPreviewNotAvailableInPlaceholder();
                return;
            }
            if (this.tabCount > 1 || this.displaySingleResultInTabset === true) {
                this.displayTabset();
                this.tabs.forEach((tab: Tab) => {
                    this.tabsetReportViewers.add(tab);
                    if (tab instanceof ReportViewerTab) {
                        // Ideally we would add the images to the tab's heading panel when we construct the
                        // ReportViewerTab.  However, it works out better to wait and do it when the tab has been added
                        // to the tabset (which creates the heading panel).
                        tab.addImagesFromReportData();
                        this.layoutProvidedProps.tabCreationCallback?.(tab);
                    }
                });
            } else {
                this.displaySingleTab(ArrayUtil.getFirstElement(this.tabs));
            }
        }
        finally {
            this.showHidePanelDownload();
            this.enableDownloads();
        }
    }

    private isDivider(tabSource: ReportPreviewTabSource): boolean {
        return tabSource instanceof ReportData && tabSource.reportName === ReportPreview.DIVIDER_REPORT_NAME;
    }

    beginSearch() {
        this.displayPlaceholderViewer();
        this.placeholderReportViewer.searching = true;
        this.clearData();
    }

    endSearch(data: ModelRow|(any|ReportPreviewTabSource)[]) {
        this.placeholderReportViewer.searching = false;
        // For a PreviewDecorator, we'll try to resolve the data twice.  This is unfortunate but should be quick.  It's
        // necessary so that A) ad-hoc calls to endSearch() work without people have to create ReportData objects before
        // calling the method, and B) it lets us call the adjustReportPreviewData() callback on resolved data.
        const resolvedData = new ReportPreviewDataResolver(this.reportFieldName).resolve(data);
        this.data = resolvedData;
    }

    private get visibleReportExcelBytes(): string {
        return this.visibleReportData?.excelBytes;
    }

    private get visibleReportPdfBytes(): string {
        return this.visibleReportData?.pdfBytes;
    }

    private get visibleReportName(): string {
        return this.visibleReportData?.reportName;
    }

    private getVisibleTab(): Tab {
        // Return active tab's report data, unless active tab is a non-report tab
        if (this.tabsetReportViewers != null && this.contains(this.tabsetReportViewers)) {
            // Displaying tabset; return the active tab
            return this.tabsetReportViewers.getActiveTab();
        }
        const topLevelTabs: Tab[] = this.components.filter((component: Component) => component instanceof Tab) as Tab[];
        return ArrayUtil.getFirstElement(topLevelTabs);
    }

    /**
     * Return the visible tab's report data, unless that tab is a non-report tab (then return null).
     */
    private get visibleReportData(): ReportData {
        const visibleTab = this.getVisibleTab();
        return (visibleTab instanceof ReportViewerTab) ? visibleTab.reportData : null;
    }

    public get displaySingleResultInTabset(): boolean {
        return this._displaySingleResultInTabset ?? true;
    }

    public set displaySingleResultInTabset(value: boolean) {
        this._displaySingleResultInTabset = value;
    }
}
